import { isInvoiceOverdue, type InvoiceFilterRow } from '@/pages/invoices/invoiceFilters';
import { isOutstandingInvoice, outstandingRemainingCents } from '@/lib/outstandingInvoice';

export interface InvoiceKpiRow extends InvoiceFilterRow {
  id?: string;
  total_paid_cents?: number | null;
  kind?: string | null;
}

export interface InvoicePaymentAmount {
  invoice_id: string;
  amount_cents: number;
  payment_date: string | null;
}

export interface InvoiceKpis {
  issuedCents: number;
  issuedCount: number;
  outstandingCents: number;
  outstandingCount: number;
  overdueCents: number;
  overdueCount: number;
  paidCents: number;
  paidCount: number;
}

const COPENHAGEN = 'Europe/Copenhagen';

function copenhagenYearMonth(value: Date | string): { year: number; month: number } | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: COPENHAGEN,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  if (!year || !month) return null;
  return { year, month };
}

function inSameMonth(iso: string | null | undefined, now: Date): boolean {
  if (!iso) return false;
  const payment = copenhagenYearMonth(iso);
  const current = copenhagenYearMonth(now);
  if (!payment || !current) return false;
  return payment.year === current.year && payment.month === current.month;
}

function remainingCents(row: InvoiceKpiRow): number {
  return outstandingRemainingCents(row.total_cents, row.total_paid_cents ?? 0);
}

function isOutstanding(row: InvoiceKpiRow): boolean {
  return isOutstandingInvoice({
    kind: row.kind,
    status: row.status,
    totalCents: row.total_cents,
    paidCents: row.total_paid_cents,
  });
}

export function computeInvoiceKpis(
  rows: readonly InvoiceKpiRow[],
  now: Date,
  payments?: readonly InvoicePaymentAmount[],
): InvoiceKpis {
  const kpis: InvoiceKpis = {
    issuedCents: 0,
    issuedCount: 0,
    outstandingCents: 0,
    outstandingCount: 0,
    overdueCents: 0,
    overdueCount: 0,
    paidCents: 0,
    paidCount: 0,
  };

  for (const row of rows) {
    if (row.kind === 'credit_note') continue;
    if (inSameMonth(row.issued_at, now)) {
      kpis.issuedCents += row.total_cents;
      kpis.issuedCount += 1;
    }
    if (isOutstanding(row)) {
      kpis.outstandingCents += remainingCents(row);
      kpis.outstandingCount += 1;
    }
    if (isInvoiceOverdue(row, now)) {
      kpis.overdueCents += remainingCents(row);
      kpis.overdueCount += 1;
    }
    if (
      !payments &&
      row.status === 'paid' &&
      inSameMonth(row.paid_at ?? row.last_payment_at, now)
    ) {
      kpis.paidCents += row.total_cents;
      kpis.paidCount += 1;
    }
  }

  if (payments) {
    const excluded = new Set(
      rows
        .filter((row) => row.kind === 'credit_note' || row.status === 'refunded')
        .map((row) => row.id)
        .filter((id): id is string => Boolean(id)),
    );
    const netByInvoice = new Map<string, number>();
    for (const payment of payments) {
      if (!inSameMonth(payment.payment_date, now)) continue;
      if (excluded.has(payment.invoice_id)) continue;
      netByInvoice.set(
        payment.invoice_id,
        (netByInvoice.get(payment.invoice_id) ?? 0) + payment.amount_cents,
      );
    }
    for (const net of netByInvoice.values()) {
      if (net <= 0) continue;
      kpis.paidCents += net;
      kpis.paidCount += 1;
    }
  }

  return kpis;
}
